import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ExternalLink, Loader2, RefreshCw, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabase-function-errors";
import {
  ArtifactMessageForOs,
  OPERATING_SYSTEM_APP_SAFE_SELECT,
  OPERATING_SYSTEM_JOB_SAFE_SELECT,
  OperatingSystemApp,
  OperatingSystemJob,
  extractArtifactTitle,
  getOperatingSystemFeedbackLabel,
  findOperatingSystemForArtifact,
  getOperatingSystemStepLabel,
  hasOperatingSystemRunnableRuntime,
  isActiveOsJobStatus,
  isActiveOsStatus,
  isSupportManagedAppRecovery,
  openOperatingSystemRunInNewTab,
} from "@/lib/operating-systems";

type OsActionMode = "create" | "retry" | "open" | "busy";

interface ArtifactOperatingSystemActionProps {
  selectedArtifact: ArtifactMessageForOs;
  allArtifacts: ArtifactMessageForOs[];
  messages: ArtifactMessageForOs[];
  conversationId?: string | null;
  artifactTitle?: string | null;
  boardMode?: boolean;
  isAnonymous?: boolean;
  onAuthBlocked?: () => void;
}

export function ArtifactOperatingSystemAction({
  selectedArtifact,
  allArtifacts,
  messages,
  conversationId,
  artifactTitle,
  boardMode = false,
  isAnonymous = false,
  onAuthBlocked,
}: ArtifactOperatingSystemActionProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [apps, setApps] = useState<OperatingSystemApp[]>([]);
  const [jobs, setJobs] = useState<OperatingSystemJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);

  const loadOperatingSystems = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    try {
      const { data: appRows, error: appError } = await supabase
        .from("operating_system_apps")
        .select(OPERATING_SYSTEM_APP_SAFE_SELECT)
        .eq("source_conversation_id", conversationId)
        .neq("status", "archived")
        .order("updated_at", { ascending: false });

      if (appError) throw appError;
      const nextApps = (appRows || []) as unknown as OperatingSystemApp[];
      setApps(nextApps);

      const latestJobIds = nextApps
        .map((app) => app.latest_job_id)
        .filter((id): id is string => !!id);

      if (latestJobIds.length === 0) {
        setJobs([]);
        return;
      }

      const { data: jobRows, error: jobError } = await supabase
        .from("operating_system_jobs")
        .select(OPERATING_SYSTEM_JOB_SAFE_SELECT)
        .in("id", latestJobIds);

      if (jobError) throw jobError;
      setJobs((jobRows || []) as unknown as OperatingSystemJob[]);
    } catch (error) {
      console.error("Failed to load operating systems:", error);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    void loadOperatingSystems();
  }, [loadOperatingSystems]);

  const matchedApp = useMemo(
    () => findOperatingSystemForArtifact({ selectedArtifact, allArtifacts, messages, apps }),
    [allArtifacts, apps, messages, selectedArtifact],
  );

  const latestJob = useMemo(
    () => jobs.find((job) => job.id === matchedApp?.latest_job_id) || null,
    [jobs, matchedApp?.latest_job_id],
  );

  const isSelectedSource = matchedApp?.source_message_id === selectedArtifact.id;
  const hasSelectedSourceRecoveryHold = isSelectedSource && isSupportManagedAppRecovery(latestJob);
  const hasActiveJob = !!(
    matchedApp &&
    isSelectedSource &&
    (isActiveOsStatus(matchedApp.status) || isActiveOsJobStatus(latestJob?.status))
  );

  useEffect(() => {
    if (!hasActiveJob) return;
    const timer = window.setInterval(() => {
      void loadOperatingSystems();
    }, 8000);
    return () => window.clearInterval(timer);
  }, [hasActiveJob, loadOperatingSystems]);

  const actionMode: OsActionMode = (() => {
    if (hasSelectedSourceRecoveryHold) return "busy";
    if (hasActiveJob) return "busy";
    if (!matchedApp || !isSelectedSource) return "create";
    if (matchedApp.status === "update_auth_blocked" && hasOperatingSystemRunnableRuntime(matchedApp)) return "open";
    if (matchedApp.status === "auth_blocked" || matchedApp.status === "update_auth_blocked") return "busy";
    if (matchedApp.status === "failed") return "retry";
    if (matchedApp.status === "update_failed" && hasOperatingSystemRunnableRuntime(matchedApp)) return "open";
    if (matchedApp.status === "ready") return "open";
    return "create";
  })();

  const label = (() => {
    if (hasSelectedSourceRecoveryHold) return "Build unavailable";
    if (actionMode === "busy") {
      if (matchedApp?.status === "auth_blocked" || matchedApp?.status === "update_auth_blocked") return "Auth paused";
      const progress = latestJob?.progress_percent ?? 0;
      const action = matchedApp?.status === "updating" || latestJob?.job_type === "update" ? "Updating" : "Building";
      return progress > 0 ? `${action} ${progress}%` : action;
    }
    if (actionMode === "open") return "Open full screen";
    if (actionMode === "retry") return "Restart build";
    return "Create App";
  })();

  const startJob = async (mode: Exclude<OsActionMode, "open" | "busy">) => {
    if (isAnonymous) {
      onAuthBlocked?.();
      return;
    }
    if (!selectedArtifact?.id || starting || hasSelectedSourceRecoveryHold) return;

    setStarting(true);
    try {
      const { data, error } = await supabase.functions.invoke("start-os-job", {
        body: {
          artifactMessageId: selectedArtifact.id,
          artifactName: artifactTitle || extractArtifactTitle(selectedArtifact.content),
          operation: "create",
          operatingSystemId: mode === "retry" ? matchedApp?.id : undefined,
          retry: mode === "retry",
        },
      });

      if (error) throw error;

      toast({
        title: "App build started",
        description: "You can keep working while the workspace builds in the background.",
      });
      await loadOperatingSystems();

      if (data?.operating_system_id) {
        navigate(`/applications/${data.operating_system_id}`);
      }
    } catch (error) {
      console.error("Failed to start App job:", error);
      const description = await getSupabaseFunctionErrorMessage(error, "Unable to start the App job.");
      toast({
        title: "App job failed to start",
        description,
        variant: "destructive",
      });
    } finally {
      setStarting(false);
    }
  };

  const handleClick = () => {
    if (actionMode === "busy") return;
    if (actionMode === "open" && matchedApp) {
      const opened = openOperatingSystemRunInNewTab(matchedApp.id);
      if (!opened) {
        toast({
          title: "Unable to open new tab",
          description: "Allow pop-ups for Applyt and try again.",
          variant: "destructive",
        });
      }
      return;
    }
    void startJob(actionMode as Exclude<OsActionMode, "open" | "busy">);
  };

  const icon = (() => {
    if (hasSelectedSourceRecoveryHold) return <AlertCircle className="h-3 w-3" />;
    if (starting || loading || actionMode === "busy") return <Loader2 className="h-3 w-3 animate-spin" />;
    if (actionMode === "open") return <ExternalLink className="h-3 w-3" />;
    if (actionMode === "retry") return <RefreshCw className="h-3 w-3" />;
    return <Rocket className="h-3 w-3" />;
  })();

  const title = hasSelectedSourceRecoveryHold
    ? "This build cannot be retried safely while its prior usage or changes remain uncertain."
    : actionMode === "busy"
    ? getOperatingSystemStepLabel(latestJob?.current_step) || getOperatingSystemFeedbackLabel(matchedApp?.latest_job_feedback) || "App build is running"
    : undefined;

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        variant={actionMode === "open" ? "default" : "outline"}
        size="sm"
        onClick={handleClick}
        disabled={starting || actionMode === "busy"}
        title={title}
        className={actionMode === "open"
          ? "min-h-10 md:min-h-0 gap-1.5 border border-amber-300/60 bg-gradient-to-r from-amber-500 to-amber-600 text-xs text-white shadow-sm transition-all hover:from-amber-600 hover:to-amber-700 hover:shadow-md"
          : boardMode
            ? "min-h-10 md:min-h-0 text-xs gap-1.5 border-[hsl(var(--brand-accent-warm-text))]/35 bg-card text-[hsl(var(--brand-accent-warm-text))] hover:bg-[hsl(var(--brand-accent-warm-bg))]"
            : "min-h-10 md:min-h-0 text-xs gap-1.5"}
      >
        {icon}
        {label}
      </Button>
    </div>
  );
}
